import React, { useEffect, useState, useContext, ReactElement } from "react";
import { Paper, Typography, TableRow, Tooltip } from "@material-ui/core";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import UserContext from "../../../UserContext";
import TrTable from "../../../components/shared/controls/TrTable";
import TrSnackbar from "../../../components/shared/controls/TrSnackbar";
import StepHandler from "../../../models/shared/StepHandler";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../../models/shared/TrTableBodyData";
import FinancingRequestStudent from "../../../models/financing-request/FinancingRequestStudent";
import FinancingRequestApiClient from "../../../api/FinancingRequestApiClient";
import { hrDate } from "../../../utilities/utilities";

interface Props extends StepHandler {
  dateFrom: Date | null;
  students: FinancingRequestStudent[] | null;
  onChange: (students: FinancingRequestStudent[]) => void;
}

export default function FinancingRequestStudentStep({
  dateFrom,
  buttons,
  onPrevious,
  onNext,
  goToPreviousStep,
  goToNextStep,
  onStepperSave,
  students,
  onChange
}: Props) {
  const { user } = useContext(UserContext);

  useEffect(() => {}, [students]);

  useEffect(() => {
    async function getStudents() {
      const apiClient = new FinancingRequestApiClient();
      const students = await apiClient.getStudentsForNewFinancingRequest(
        dateFrom!
      );

      onChange(students);
    }

    if (students == null || students.length === 0) {
      getStudents();
    }
  }, []);

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
    variant: snackbarVariant
  } = useSnackbar();

  const handlePreviousStep = () => {
    if (goToPreviousStep) {
      goToPreviousStep();
    }
  };

  const handleNextStep = () => {
    if (students && students.filter(s => s.isSelected).length === 0) {
      openSnackbar();
    } else {
      goToNextStep && goToNextStep();
    }
  };

  const handleSelectChange = (studentIds: string[]) => {
    if (students != null) {
      let studentsProp = [...students];
      studentsProp.forEach(s => {
        s.isSelected = studentIds.includes(s.id.toString());
      });

      onChange(studentsProp);
    }
  };

  const handleUpdateState = () => {
    onChange(students || new Array<FinancingRequestStudent>());
  };

  onPrevious && onPrevious(handlePreviousStep);
  onNext && onNext(handleNextStep);
  onStepperSave && onStepperSave(handleUpdateState);

  const tableHeadMetadata: TrTableHeadMetadata<FinancingRequestStudent>[] = [
    {
      id: "student",
      label: "Učenik",
      dataName: "lastName",
      dataFunc: (d: FinancingRequestStudent) => (
        <>
          <Typography variant="body2">
            {d.lastName + " " + d.firstName}
          </Typography>
          <Typography variant="body2">{hrDate(d.birthDate)}</Typography>
        </>
      )
    },
    { id: "oib", label: "OIB", dataName: "oib" },
    {
      id: "schoolClass",
      label: "Razred",
      dataName: "schoolClass",
      dataFunc: (d: FinancingRequestStudent) => (
        <>
          <Typography variant="body2">{d.programName}</Typography>
          <Typography variant="body2">
            {d.schoolClass + " - " + d.sectionMark}
          </Typography>
        </>
      )
    },
    {
      id: "vrstaOdjeljenja",
      label: "Vrsta odjeljenja",
      dataFunc: (d: FinancingRequestStudent) => (
        <Typography variant="body2">{d.sectionTypeName}</Typography>
      )
    }
  ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: students
  };

  const handleBodyRowCreated = (tableRow: ReactElement, rowData: any) => {
    return rowData.isEnabled ? (
      <TableRow key={tableRow.key} {...tableRow.props} />
    ) : (
      <Tooltip title="Za učenika je već kreiran zahtjev." placement="top">
        <TableRow key={tableRow.key} {...tableRow.props} />
      </Tooltip>
    );
  };

  const preSelectedIds: string[] =
    students != null
      ? students.filter(s => s.isSelected).map(s => s.id.toString())
      : [];

  const disabledIds: string[] =
    students != null
      ? students.filter(s => !s.isEnabled).map(s => s.id.toString())
      : [];

  return (
    <div>
      <Paper>
        <TrTable
          hover
          headMetadata={tableHeadMetadata}
          bodyData={tableBodyData}
          enableSelect={true}
          enableAllSelect={true}
          preSelectedIds={preSelectedIds}
          disabledIds={disabledIds}
          onSelectChange={(studentIds: string[]) =>
            handleSelectChange(studentIds)
          }
          onBodyRowCreated={handleBodyRowCreated}
        />
      </Paper>
      {buttons}
      <TrSnackbar
        variant={"error"}
        isOpened={isSnackbarOpened}
        message={
          "Ne možete prijeći na sljedeći korak dok ne odaberete barem jednog učenika."
        }
        onClose={closeSnackbar}
      />
    </div>
  );
}
